import React, { useContext } from 'react';

import { AppContext } from 'src/components/app-context';
import { usePriceRowContext } from '../../../price-row-context';

import { FontEmphasis, FontWeight } from '@prisjakt/ui/text';
import { CaptionText } from '@prisjakt/ui/text/variants';

import { MediumUp, MobileOnly } from 'src/ui/block';
import { OptionTypeEnum } from 'src/components/filter-dropdown';

import {
  ShippingType,
  getShippingDays,
  StockStatusEnum,
  getDeliveryTimeText,
  isShippingInstorePickup,
} from './utils';

import { ShippingCollection, StockStatus } from 'src/types';

import { StyledDeliveryText, StyledDeliveryTime, StyledLabel } from './styles';

interface IDeliveryTimeProps {
  shipping?: ShippingCollection | null;
  stockStatus?: StockStatus | null;
}

export const DeliveryTime = (props: IDeliveryTimeProps) => {
  const { shipping, stockStatus } = props;

  const { t } = useContext(AppContext);

  const { sorting } = usePriceRowContext();

  let sort = ShippingType.CHEAPEST;

  if (sorting === OptionTypeEnum.FASTEST_DELIVERY) {
    sort = ShippingType.FASTEST;
  }

  const getLabel = () => {
    if (
      stockStatus === StockStatusEnum.IN_STOCK ||
      stockStatus === StockStatusEnum.INCOMING
    ) {
      return `${t('product', 'delivery-time.label-experiment')} `;
    }
    return '';
  };

  const label = getLabel();

  const isInstorePickup = isShippingInstorePickup(sort, shipping);
  const shippingDays = getShippingDays(t, sort, shipping);
  const deliveryText = getDeliveryTimeText(t, shippingDays, stockStatus);
  const highlightText =
    !isInstorePickup &&
    (stockStatus === StockStatusEnum.IN_STOCK ||
      stockStatus === StockStatusEnum.INCOMING);

  const deliveryEmphasis = highlightText
    ? FontEmphasis.HIGH
    : FontEmphasis.MEDIUM;

  const deliveryFontWeight = highlightText
    ? FontWeight.HEAVIEST
    : FontWeight.NORMAL;

  // If no delivery time is available,
  // no delivery text should be visible unless sorting on fastest delivery.
  const showDeliveryTime =
    shippingDays || isInstorePickup || sort === ShippingType.FASTEST;

  const renderMobileDeliveryTime = () => {
    if (!showDeliveryTime) {
      return null;
    }

    return (
      <MobileOnly>
        <div>
          {!isInstorePickup && (
            <StyledLabel emphasis={FontEmphasis.MEDIUM}>{label}</StyledLabel>
          )}
          <CaptionText emphasis={deliveryEmphasis} weight={deliveryFontWeight}>
            {isInstorePickup
              ? t('product', `delivery-option.pickup-instore`)
              : deliveryText}
          </CaptionText>
        </div>
      </MobileOnly>
    );
  };

  const renderDesktopDeliveryTime = () => {
    if (!showDeliveryTime) {
      return null;
    }

    return (
      <MediumUp>
        <StyledDeliveryText>
          {!isInstorePickup && (
            <CaptionText emphasis={FontEmphasis.MEDIUM}>{label}</CaptionText>
          )}
          <CaptionText emphasis={deliveryEmphasis} weight={deliveryFontWeight}>
            {isInstorePickup
              ? t('product', `delivery-option.pickup-instore`)
              : deliveryText}
          </CaptionText>
        </StyledDeliveryText>
      </MediumUp>
    );
  };

  return (
    <StyledDeliveryTime>
      {renderMobileDeliveryTime()}
      {renderDesktopDeliveryTime()}
    </StyledDeliveryTime>
  );
};
